import {
  Button,
  Dialog,
  Classes,
  Spinner,
  MenuItem,
  InputGroup,
} from "@blueprintjs/core";
import React, { useState, useEffect, useRef } from "react";
import { GET_CRF_FULL, GET_CENTRES } from "../../queries";
import { client } from "../../apolloClient";
import 'react-checkbox-tree/lib/react-checkbox-tree.css';
import CheckboxTree from 'react-checkbox-tree';
import { downloadCsvFile, downloadSpsFile } from "../../utils/DownloadFile";
import {
  API_URL,
  EXPORT_TYPE_SECTION_ENCODED,
  EXPORT_TYPE_VARIABLE_ENCODED,
  EXPORT_TYPE_VARIABLE_LABEL,
  EXPORT_SINTAX,
  EXPORT_MAP,
  PATIENT_STATUS_CLOSED,
  PATIENT_STATUS_NEW,
  PATIENT_STATUS_INPROGRESS,
  PATIENT_STATUS_SIGNED,
  PATIENT_STATUS_MONITORING,
} from "../../config";
import { getToken, getSEK } from "../../services";
import "./_ModalExport.scss";
import { Select } from "@blueprintjs/select";
import FieldFilter from './FieldFilter';

const ModalExport = ({ t, isOpen, toggleFunction, entityId, exportType }) => {
  const [state, setState] = useState({
    autoFocus: true,
    canEscapeKeyClose: true,
    canOutsideClickClose: true,
    enforceFocus: true,
    usePortal: true,
    ready: false,
    fetched: false,
    loading: false,
    crf: [],
    tree:  [],
    checked: [],
    expanded: [],
    centres: [],
    deferredJob: null,
    deferredJobs: [],
    deferredQuota: {
      queuedCount: 0,
      queuedLimit: null,
      reached: false,
    },
    deferredMessage: "",
    search: {
      centre: { id: null, name: t("select.centre") },
      code: "",
      branch: "",
      state: { id: "", text: "All states" },
      fields: []
    },
  });
  const deferredSubmitInFlight = useRef(false);

  const states = [
    { id: "", text: "All States" },
    { id: PATIENT_STATUS_NEW, text: "New" },
    { id: PATIENT_STATUS_INPROGRESS, text: "In Progress" },
    { id: PATIENT_STATUS_MONITORING, text: "Monitoring" },
    { id: PATIENT_STATUS_CLOSED, text: "Closed" },
    { id: PATIENT_STATUS_SIGNED, text: "Signed" },
  ];

  useEffect(() => {
    if (!isOpen || !entityId) {
      return undefined;
    }

    refreshDeferredJobs();
    const interval = setInterval(() => refreshDeferredJobs(), 5000);
    return () => clearInterval(interval);
  }, [isOpen, entityId]);

  const handleClose = () => {
    const activeJobs = state.deferredJobs.filter((job) => job.status === "queued" || job.status === "running" || job.status === "retrying");
    if (activeJobs.length > 0) {
      window.alert("La exportación diferida continuará en segundo plano. Puede volver a esta ventana más tarde para ver el progreso y descargar el fichero. Las descargas de más de 24 horas serán eliminadas automáticamente.");
    }
    toggleFunction();
    setState({
      autoFocus: true,
      canEscapeKeyClose: true,
      canOutsideClickClose: true,
      enforceFocus: true,
      usePortal: true,
      ready: false,
      fetched: false,
      loading: false,
      crf: [],
      tree:  [],
      checked: [],
      expanded: [],
      centres: state.centres,
      deferredJob: null,
      deferredJobs: [],
      deferredQuota: {
        queuedCount: 0,
        queuedLimit: null,
        reached: false,
      },
      deferredMessage: "",
      search: {
        text: "",
        centre: { id: null, name: t("select.centre") },
        code: "",
        branch: "",
        state: { id: "", text: "All states" },
        fields: []
      },
    });
  }

  const downloadExport = () => {
    setState({...state, loading: true });
    downloadCsv(entityId, state.crf.name, exportType);
  }

  const getDeferredProgress = (job) => {
    if (job.progressTotal) {
      return `${job.progressPercent}%`;
    }
    if (job.position) {
      return `en cola #${job.position}`;
    }
    return job.status;
  };

  const getDeferredDate = (job) => {
    const date = job.finishedAt || job.startedAt || job.queuedAt || job.createdAt;
    if (!date) {
      return "";
    }
    return new Date(date).toLocaleString();
  };

  const getDeferredSize = (job) => {
    if (!job.outputSizeKb) {
      return "-";
    }
    return `${job.outputSizeKb} KB`;
  };

  const getDeferredSelectionTitle = (job) => {
    if (job.selectionFields && job.selectionFields.length > 0 && state.tree.length > 0) {
      const labels = [];
      const selected = new Set(job.selectionFields);

      const collectLabels = (nodes, path = []) => {
        nodes.forEach((node) => {
          const nextPath = node.label ? path.concat(node.label) : path;
          if (selected.has(node.value)) {
            labels.push(nextPath.join(" / "));
          }
          if (node.children) {
            collectLabels(node.children, nextPath);
          }
        });
      };

      collectLabels(state.tree);
      if (labels.length > 0) {
        return labels.join("\n");
      }
    }

    return job.selectionDetail || job.selectionSummary || "Sin informacion de seleccion";
  };

  const getDeferredStatusText = (job) => {
    const statuses = {
      queued: "En cola",
      running: "En curso",
      retrying: "Reintentando",
      success: "Completada",
      failed: "Fallida",
      cancelled: "Cancelada",
      cancel_requested: "Cancelando",
      expired: "Expirada",
      stale: "Interrumpida",
    };
    return statuses[job.status] || job.status;
  };

  const getDeferredStatusClass = (job) => {
    if (job.status === "success") {
      return "success";
    }
    if (job.status === "failed" || job.status === "cancelled" || job.status === "expired" || job.status === "stale") {
      return "error";
    }
    return "active";
  };

  const refreshDeferredJobs = () => {
    if (!entityId) {
      return;
    }

    fetch(`${API_URL}/crf/export-jobs?crfId=${encodeURIComponent(entityId)}`, {
      method: "GET",
      mode: "cors",
      cache: "no-cache",
      headers: {
        authorization: `${getToken()}$_${getSEK()}`,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.ok) {
          setState((prevState) => {
            const quota = data.quota || prevState.deferredQuota;
            return {
              ...prevState,
              deferredJobs: data.jobs || [],
              deferredQuota: quota,
              deferredMessage: quota.reached && !prevState.deferredMessage
                ? `Limite de exportaciones en cola alcanzado (${quota.queuedCount}/${quota.queuedLimit})`
                : prevState.deferredMessage,
            };
          });
        }
      })
      .catch((error) => console.error(error));
  };

  const getDeferredFileName = (response, fallbackName) => {
    const disposition = response.headers.get("content-disposition");
    if (disposition && disposition.indexOf("filename=") >= 0) {
      return disposition.split("filename=")[1].replace(/"/g, "");
    }
    return fallbackName;
  };

  const downloadDeferredFile = async (job) => {
    const response = await fetch(`${API_URL}/crf/export-jobs/${job.id}/download`, {
      method: "GET",
      mode: "cors",
      cache: "no-cache",
      headers: {
        authorization: `${getToken()}$_${getSEK()}`,
      },
    });

    if (!response.ok) {
      throw new Error("Deferred export download failed");
    }

    const blob = await response.blob();
    const filename = getDeferredFileName(response, job.filename || `${state.crf.name}-export.csv`);
    const href = await URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = href;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const deleteDeferredFile = (job) => {
    const confirmed = window.confirm("Se borrara esta exportacion diferida y su fichero si existe. Esta accion no se puede deshacer.");
    if (!confirmed) {
      return;
    }

    fetch(`${API_URL}/crf/export-jobs/${job.id}/delete`, {
      method: "DELETE",
      mode: "cors",
      cache: "no-cache",
      headers: {
        authorization: `${getToken()}$_${getSEK()}`,
      },
    })
      .then((response) => response.json().then((data) => ({ response, data })))
      .then(({ response, data }) => {
        if (!response.ok || !data.ok) {
          throw new Error(data.error || "Deferred export delete failed");
        }

        setState((prevState) => ({
          ...prevState,
          deferredJobs: prevState.deferredJobs.filter((deferredJob) => deferredJob.id !== job.id),
          deferredQuota: data.quota || prevState.deferredQuota,
          deferredMessage: "Exportacion diferida borrada",
        }));
      })
      .catch((error) => {
        console.error(error);
        setState((prevState) => ({
          ...prevState,
          deferredMessage: error.message || "Error",
        }));
      });
  };

  const pollDeferredExport = (jobId) => {
    fetch(`${API_URL}/crf/export-jobs/${jobId}`, {
      method: "GET",
      mode: "cors",
      cache: "no-cache",
      headers: {
        authorization: `${getToken()}$_${getSEK()}`,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (!data.ok) {
          throw new Error(data.error || "Deferred export status failed");
        }

        const job = data.job;
        const progress = getDeferredProgress(job);

        setState((prevState) => ({
          ...prevState,
          loading: false,
          deferredJob: job,
          deferredJobs: [job].concat(prevState.deferredJobs.filter((deferredJob) => deferredJob.id !== job.id)),
          deferredMessage: progress,
        }));

        if (job.status === "success") {
          downloadDeferredFile(job).catch((error) => console.error(error));
        } else if (job.status !== "failed" && job.status !== "cancelled" && job.status !== "expired") {
          setTimeout(() => pollDeferredExport(jobId), 5000);
        }
      })
      .catch((error) => {
        console.error(error);
        setState((prevState) => ({
          ...prevState,
          loading: false,
          deferredMessage: "Error",
        }));
      });
  };

  const downloadDeferredExport = () => {
    if (deferredSubmitInFlight.current) {
      return;
    }
    if (state.deferredQuota && state.deferredQuota.reached) {
      setState((prevState) => ({
        ...prevState,
        deferredMessage: `Limite de exportaciones en cola alcanzado (${prevState.deferredQuota.queuedCount}/${prevState.deferredQuota.queuedLimit})`,
      }));
      return;
    }

    deferredSubmitInFlight.current = true;
    setState((prevState) => ({...prevState, loading: true, deferredMessage: "" }));
    fetch(`${API_URL}/crf/export-jobs`, {
      method: "POST",
      mode: "cors",
      cache: "no-cache",
      headers: {
        authorization: `${getToken()}$_${getSEK()}`,
      },
      body: JSON.stringify({ crfId: entityId, exportType, fields: state.checked, filter: state.search })
    })
      .then((response) => response.json().then((data) => ({ response, data })))
      .then(({ response, data }) => {
        if (!response.ok) {
          const message = data.code === "queued_limit" && data.quota
            ? `Limite de exportaciones en cola alcanzado (${data.quota.queuedCount}/${data.quota.queuedLimit})`
            : data.error || "Deferred export failed";
          throw new Error(message);
        }
        return data;
      })
      .then((data) => {
        deferredSubmitInFlight.current = false;
        if (!data.ok) {
          throw new Error(data.error || "Deferred export failed");
        }
        setState((prevState) => ({
          ...prevState,
          loading: false,
          deferredJob: data.job,
          deferredJobs: [data.job].concat(prevState.deferredJobs.filter((job) => job.id !== data.job.id)),
          deferredQuota: data.quota || prevState.deferredQuota,
          deferredMessage: data.job.position ? `en cola #${data.job.position}` : data.job.status,
        }));
        pollDeferredExport(data.job.id);
      })
      .catch((error) => {
        deferredSubmitInFlight.current = false;
        console.error(error);
        setState((prevState) => ({
          ...prevState,
          loading: false,
          deferredMessage: error.message || "Error",
        }));
        refreshDeferredJobs();
      });
  };

  const cancelDeferredExport = (job) => {
    setState((prevState) => ({...prevState, loading: true, deferredMessage: "" }));
    fetch(`${API_URL}/crf/export-jobs/${job.id}/cancel`, {
      method: "POST",
      mode: "cors",
      cache: "no-cache",
      headers: {
        authorization: `${getToken()}$_${getSEK()}`,
      },
    })
      .then((response) => response.json().then((data) => ({ response, data })))
      .then(({ response, data }) => {
        if (!response.ok || !data.ok) {
          throw new Error(data.error || "Deferred export cancel failed");
        }

        setState((prevState) => ({
          ...prevState,
          loading: false,
          deferredJob: data.job,
          deferredJobs: prevState.deferredJobs.map((deferredJob) => deferredJob.id === data.job.id ? data.job : deferredJob),
          deferredQuota: data.quota || prevState.deferredQuota,
          deferredMessage: "Exportacion diferida cancelada",
        }));
        refreshDeferredJobs();
      })
      .catch((error) => {
        console.error(error);
        setState((prevState) => ({
          ...prevState,
          loading: false,
          deferredMessage: error.message || "Error",
        }));
      });
  };

  const downloadCsv = async (id, name, type) => {
    fetch(`${API_URL}/crf/export/${id}/${type}`, {
      method: "POST",
      mode: "cors",
      cache: "no-cache",
      headers: {
        authorization: `${getToken()}$_${getSEK()}`,
      },
      
      body: JSON.stringify({ fields:  state.checked, filter: state.search })
    })
      .then((response) => {
        console.log("Se ejecuta la función downloadCsv (1then) en dashboard")
        const contentType = response.headers.get("content-type");
        if (!response.ok) {
          if (contentType && contentType.includes("application/json")) {
            return response.json().then((data) => {
              throw new Error(data.error || "No se pudo completar la exportacion");
            });
          }
          throw new Error("No se pudo completar la exportacion");
        }
        if (
          !contentType ||
          !(
            contentType.includes("text/csv") ||
            contentType.includes("text/plain")
          )
        ) {
          throw new TypeError("La respuesta de exportacion no contiene un CSV valido");
        }
        return response.text();
      })
      .then((data) => {
        console.log("Se ejecuta la función downloadCsv (2then) en dashboard")
        handleClose();
        if (type == EXPORT_SINTAX) {
          const filename = `${name
            .trim()
            .replace(/\s/g, "-")}-SPS-${new Date().getTime()}`;
          downloadSpsFile(data, filename);
        } else if (type == EXPORT_MAP) {
          const filename = `${name
            .trim()
            .replace(/\s/g, "-")}-MAP-${new Date().getTime()}`;
          downloadCsvFile(data, filename);
        } else {
          let filename = name.trim().replace(/\s/g, "-");
          filename =
            type == EXPORT_TYPE_VARIABLE_ENCODED ||
            type == EXPORT_TYPE_VARIABLE_LABEL
              ? filename + "-SPSS"
              : filename;
          filename =
            type == EXPORT_TYPE_VARIABLE_ENCODED ||
            type == EXPORT_TYPE_SECTION_ENCODED
              ? filename + "-ENCODED"
              : filename;
          filename = filename + `-${new Date().getTime()}`;
          downloadCsvFile(data, filename);
        }
      })
      .catch((error) => {
        console.error(error);
        setState((prevState) => ({
          ...prevState,
          loading: false,
          deferredMessage: error.message || "Error",
        }));
      });
  };

  const getFormTree = (form, sheet) => {
    if(Array.isArray(form)) {
      return form.reduce((accumulator, container) => {
        const formTree = getFormTree(container, sheet);
        if (formTree !== null && typeof(formTree) !== 'undefined') {
          accumulator.push(formTree)
        }
        return accumulator;
      }, []);
    } else if(typeof form === 'object') {
      if(form.input === true && !form.hasOwnProperty('components')) {
        const checked = state.checked;
        if(!checked.includes(`${form.key}_${sheet}`))
          checked.push(`${form.key}_${sheet}`)

        return {
          label: form.label,
          value: `${form.key}_${sheet}`,
        }
      } else if(form.hasOwnProperty('components')) {
        let childrens = getFormTree(form.components, sheet)
        if (form.label) {
          return childrens && childrens.length > 0 ? {
            label: form.label,
            value: `${form.id}_${sheet}`,
            children: childrens
          }: null
        } else {
          return childrens && childrens.length > 0 ? childrens: null;
        }
      } else if(form.hasOwnProperty('columns')) {
        let childrens = form.columns.reduce((accumulator, column) => {
          Array.prototype.push.apply(accumulator, getFormTree(column.components, sheet));
          return accumulator;
        }, [])
        if ( childrens && childrens.length > 0) {
          return {
            label: form.label,
            value: `${form.id}_${sheet}`,
            children: childrens && childrens.length > 0 ? childrens: null
          }
        } else {
          return null;
        }
        
      } else if(form.hasOwnProperty('rows')) {
        let childrens = form.rows.reduce((accumulator, row, index) => {
          row.forEach(element => {
            Array.prototype.push.apply(accumulator, getFormTree(element.components, sheet));
          });
          return accumulator;
        }, []);

        return {
          label: form.label,
          value: `${form.id}_${sheet}`,
          children: childrens
        }
      }
    }

    return null;
  }

  if(state.centres.length == 0) {
    client
      .query({
        query: GET_CENTRES,
        variables: { elementsPerPage: 1000, page: 1 },
      })
      .then((res) => {
        if(res.data.centres.objects[0]) {
          if (res.data.centres.objects[0].id !== null) 
            res.data.centres.objects.unshift({
              id: null,
              name: t("select.centre"),
            });
            setState({
              ...state,
              centres: res.data.centres.objects
            });
        }
        
      });
  }

  const removeDuplicateValues = (obj) => {
    let valuesSet = new Set(); 
    function traverse(node) {
      if (node.value) {
        let uniqueValue = node.value;
        let counter = 1;
        // Checks if the value already exists
        while (valuesSet.has(uniqueValue)) {
          
          let parts = node.value.split("_");
          let number = parseInt(parts[0].match(/\d/));
          let incrementedNumber;

          if (isNaN(number)) {
            incrementedNumber = counter;
          } else {
            incrementedNumber = number + counter;
          }

          parts[0] = parts[0].replace(String(number), "");

          uniqueValue = parts[0] + String(incrementedNumber) + "_" + parts[1];
          counter++;
        }

        // Add the single value to the set
        valuesSet.add(uniqueValue);
        // Update the node value to the unique value
        node.value = uniqueValue;

      }
      
      // Recursively calls traverse for each child of the current node
      if (node.children) {
        node.children.forEach((child) => traverse(child));
      }
    }

    obj.forEach((node) => traverse(node));

    return obj;
  }


  if (!state.fetched && entityId)
    client
      .query({
        query: GET_CRF_FULL,
        variables: {id: entityId},
      })
      .then((res) => {
        setState((prevState) => ({
          ...prevState,
          fetched: true,
          loading: false,
          crf: res.data.crf,
          tree: res.data.crf.visits.edges.map(visit => {
            let childrens = visit.node.sheets.edges.map(sheet => {
              const form = JSON.parse(sheet.node.formBuild)

              if(form.components && form.components.length > 0) {
                let sheetComponents = getFormTree(form.components, sheet.node.id);
                if (sheetComponents && sheetComponents.length > 0) {
                  return {
                    label: sheet.node.name,
                    value: sheet.node.id,
                    children: getFormTree(form.components, sheet.node.id)
                  }
                } else {
                    return {
                      label: sheet.node.name,
                      value: sheet.node.id,
                      disabled: true
                    }
                }
              } else {
                return {
                  label: sheet.node.name,
                  value: sheet.node.id,
                  disabled: true
                }
              }
            });
            removeDuplicateValues(childrens);
            return childrens && childrens.length > 0 ? {
              label: visit.node.name,
              value: visit.node.id,
              children: childrens
            }: {
              label: visit.node.name,
              value: visit.node.id,
              disabled: true
            }
          })
        }));
        refreshDeferredJobs();

      });

  // Centre search options

  const renderItem = (item, { modifiers, handleClick }) => {
    return (
      <MenuItem
        active={modifiers.active}
        key={item.id}
        label={item.code}
        onClick={handleClick}
        text={item.name}
        shouldDismissPopover={false}
      />
    );
  };

  const renderItemState = (item, { modifiers, handleClick }) => {
    return (
      <MenuItem
        active={modifiers.active}
        key={item.id}
        onClick={handleClick}
        text={item.text}
        shouldDismissPopover={false}
      />
    );
  };

  const areCentreEquals = (a, b) => {
    let equal = a.id === b.id ? true : false;

    return equal;
  };

  const areStateEquals = (a, b) => {
    let equal = a.id === b.id ? true : false;

    return equal;
  };

  const handleSelectChange = (centre) => {

    setState((prevState) => ({
      ...prevState,
      search: {
        ...prevState.search,
        centre,
      },
    }));
  };

  const handleSelectStateChange = (state) => {
    setState((prevState) => ({
      ...prevState,
      search: {
        ...prevState.search,
        state,
      },
    }));
  };

  const handleFilterCentre = (query, centre) =>
    centre.name.indexOf(query) >= 0

  const handleChange = (event) => {
    const { name, value } = event.target;
    setState((prevState) => ({
      ...prevState,
      search: {
        ...prevState.search,
        [name]: value,
      },
    }));
  };

  const addFieldFilter = () => {
    const fields = state.search.fields.slice();
    fields.push({visit: "", sheet: "", field: "", comparator: null, value: "" });
    setState((prevState) => ({
      ...prevState,
      search: {
        ...prevState.search,
        fields,
      },
    }));
  };


  const removeFieldFilter = (index) => {
    const fields = state.search.fields.slice();
    fields.splice(index, 1);

    setState((prevState) => ({
      ...prevState,
      search: {
        ...prevState.search,
        fields: fields
      },
    }));
  };

  const handleSelectField = (field, index) => {
    const fields = state.search.fields;
    fields[index] = field;

    setState((prevState) => ({
      ...prevState,
      search: {
        ...prevState.search,
        fields,
      },
    }));
  };

  const renderFields = () => {
    return (state.search.fields.map((field, idx)=> {
      return (
        <FieldFilter crf={state.crf} field={field} index={idx} handleSelectField={handleSelectField} removeFieldFilter={removeFieldFilter} t={t} />
      )
    }));
  }

  return (
    <Dialog
      icon="info-sign"
      onClose={handleClose}
      title={`${t("export")}`}
      isOpen={isOpen}
      className="dialog-big"
    >
      <div className={Classes.DIALOG_BODY}>

        {state.loading && (
          <div className="spinner-loader-listing">
            <Spinner
              size={80}
              spinnerColor={"#007BFF"}
              spinnerWidth={2}
              visible={true}
            />
          </div>
        )}
        {!state.loading && (
          <div>
            <div className="row pt-2 pb-3">
              <div className="col">
                <Select
                  itemsEqual={areCentreEquals}
                  itemRenderer={renderItem}
                  items={state.centres}
                  large={true}
                  itemPredicate={handleFilterCentre}
                  activeItem={state.search.centre}
                  className="w-100"
                  noResults={
                    <MenuItem
                      icon="office"
                      disabled={true}
                      text={t("No results.")}
                    />
                  }
                  onItemSelect={handleSelectChange}
                >
                  <Button
                    icon="office"
                    rightIcon="caret-down"
                    large={true}
                    fill={true}
                    text={
                      state.search.centre
                        ? state.search.centre.name
                        : t("All my centres.")
                    }
                  />
                </Select>
              </div>
              <div className="col">
                <InputGroup
                  id="code"
                  name="code"
                  onChange={handleChange}
                  value={state.search.code}
                  placeholder={t("code")}
                  large={true}
                />
              </div>
              <div className="col">
                <InputGroup
                  id="branch"
                  name="branch"
                  onChange={handleChange}
                  value={state.search.branch}
                  placeholder={t("branch")}
                  large={true}
                />
              </div>
              <div className="col">
                <Select
                  itemsEqual={areStateEquals}
                  itemRenderer={renderItemState}
                  items={states}
                  activeItem={state.search.state}
                  large={true}
                  className="w-100"
                  onItemSelect={(item) => handleSelectStateChange(item)}
                >
                  <Button
                    rightIcon="caret-down"
                    large={true}
                    fill={true}
                    text={
                      state.search.state
                        ? state.search.state.text
                        : t("All states")
                    }
                  />
                </Select>
              </div>
            </div>
            {
              renderFields()
            }
            <Button className="mb-3" intent='primary' onClick={addFieldFilter}>{t('add.fieldFilter')}</Button>
            <CheckboxTree
              nodes={state.tree}
              checked={state.checked}
              expanded={state.expanded}
              onCheck={checked => setState({...state, checked })}
              onExpand={expanded => setState({...state, expanded })}
            />
            {state.deferredMessage && (
              <div className="deferred-export-message">
                {state.deferredMessage}
              </div>
            )}
            {state.deferredJobs.length > 0 && (
              <div className="deferred-export-list">
                <div className="deferred-export-list-header">
                  <div>
                    Exportaciones diferidas
                  </div>
                  <Button small={true} minimal={true} icon="refresh" onClick={refreshDeferredJobs} />
                </div>
                <div className="deferred-export-notice">
                  Las exportaciones diferidas continuan en segundo plano. Puede cerrar esta ventana y volver mas tarde para descargar el fichero. Las descargas de mas de 24 horas se eliminaran automaticamente.
                  {state.deferredQuota && state.deferredQuota.queuedLimit !== null && (
                    <div>
                      Cola personal: {state.deferredQuota.queuedCount}/{state.deferredQuota.queuedLimit}
                    </div>
                  )}
                </div>
                {state.deferredJobs.map((job) => (
                  <div className="deferred-export-row" key={job.id}>
                    <div className="deferred-export-main">
                      <span className="deferred-export-crf" title={job.crfName || job.crfId}>
                        {job.crfName || job.crfId}
                      </span>
                      <span className={`deferred-export-status ${getDeferredStatusClass(job)}`}>
                        {getDeferredStatusText(job)}
                      </span>
                      <span className="deferred-export-progress">
                        {getDeferredProgress(job)}
                      </span>
                      <span className="deferred-export-date">
                        {getDeferredDate(job)}
                      </span>
                      <span className="deferred-export-info" title={getDeferredSelectionTitle(job)}>
                        info
                      </span>
                      <span className="deferred-export-size">
                        {getDeferredSize(job)}
                      </span>
                    </div>
                    <div className="deferred-export-actions">
                      {job.status === "success" && (
                        <Button small={true} icon="download" onClick={() => downloadDeferredFile(job)}>
                          download
                        </Button>
                      )}
                      {job.status !== "running" && job.status !== "cancel_requested" && (
                        <Button small={true} icon="trash" disabled={state.loading} onClick={() => deleteDeferredFile(job)}>
                          borrar
                        </Button>
                      )}
                      {(job.status === "queued" || job.status === "retrying" || job.status === "running") && (
                        <Button small={true} icon="disable" disabled={state.loading} onClick={() => cancelDeferredExport(job)}>
                          cancelar
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
        )}
      </div>
      <div className={Classes.DIALOG_FOOTER}>
        <div className={Classes.DIALOG_FOOTER_ACTIONS}>
          <Button onClick={() => handleClose()}>{t("close")}</Button>
          {!state.loading && (
            <Button className="deferred-export-hidden-normal-export" onClick={() => downloadExport()}>
                {t("export")}
            </Button>
          )}
          <Button
            disabled={state.loading || (state.deferredQuota && state.deferredQuota.reached)}
            title={state.deferredQuota && state.deferredQuota.reached ? "Limite de exportaciones en cola alcanzado" : ""}
            onClick={() => downloadDeferredExport()}
          >
            export diferido
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default ModalExport;
